import { useState } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddPattern() {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructions: '',
    difficulty: 'intermediate',
    yarn_requirements: '',
    hook_size: '',
    gauge: '',
    finished_size: '',
    is_public: false,
    tags: ''
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.instructions.trim()) {
      toast.error('Please enter name and instructions');
      return;
    }

    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      await axios.post(`${API}/patterns`, dataToSend, {
        withCredentials: true
      });

      toast.success('Pattern created successfully!');
      navigate('/patterns');
    } catch (error) {
      logger.error('Failed to create pattern:', error);
      toast.error('Failed to create pattern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/patterns')}
          className="mb-6 rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Patterns
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
            Create Pattern
          </h1>
          <p className="text-lg text-muted-foreground font-body">
            Share your knitting or crochet pattern
          </p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="space-y-6">
            <div>
              <Label htmlFor="name">Pattern Name *</Label>
              <Input
                data-testid="input-name"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="rounded-xl"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                data-testid="input-description"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="rounded-xl"
              />
            </div>

            <div>
              <Label htmlFor="instructions">Instructions *</Label>
              <Textarea
                data-testid="input-instructions"
                id="instructions"
                name="instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                placeholder="Row 1: Ch 20, sc in 2nd ch from hook..."
                rows={10}
                required
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="difficulty">Difficulty</Label>
                <select
                  id="difficulty"
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border-stone-200 bg-stone-50 px-3 py-2"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div>
                <Label htmlFor="hook_size">Hook/Needle Size</Label>
                <Input
                  id="hook_size"
                  name="hook_size"
                  value={formData.hook_size}
                  onChange={handleInputChange}
                  placeholder="e.g., 5mm (H-8)"
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="gauge">Gauge</Label>
                <Input
                  id="gauge"
                  name="gauge"
                  value={formData.gauge}
                  onChange={handleInputChange}
                  placeholder="e.g., 14 sts x 10 rows = 4 inches"
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="finished_size">Finished Size</Label>
                <Input
                  id="finished_size"
                  name="finished_size"
                  value={formData.finished_size}
                  onChange={handleInputChange}
                  placeholder="e.g., 50 x 60 inches"
                  className="rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="yarn_requirements">Yarn Requirements</Label>
              <Textarea
                id="yarn_requirements"
                name="yarn_requirements"
                value={formData.yarn_requirements}
                onChange={handleInputChange}
                placeholder="e.g., 800 yards worsted weight yarn"
                rows={2}
                className="rounded-xl"
              />
            </div>

            <div>
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="e.g., blanket, easy, quick"
                className="rounded-xl"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_public"
                name="is_public"
                checked={formData.is_public}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-stone-300"
              />
              <Label htmlFor="is_public" className="cursor-pointer">
                Make this pattern public (share with community)
              </Label>
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/patterns')}
            >
              Cancel
            </Button>
            <Button
              data-testid="save-pattern-button"
              type="submit"
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground"
            >
              {saving ? 'Saving...' : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Pattern
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
